import { deleteDoc, doc, setDoc } from 'firebase/firestore';
import { deleteToken, getMessaging, getToken, isSupported } from 'firebase/messaging';
import { firebase, useEmulators } from './firebase';

/**
 * Erinnerungen aufs Handy (1.13): this device asks for permission, gets its push address from
 * Firebase Cloud Messaging and registers it for the family (households/{hid}/pushTokens). The
 * service worker (public/push-sw.js) shows what arrives. Without the public web push key in the
 * build (VITE_FIREBASE_VAPID_KEY) the feature stays hidden.
 */
/**
 * The public half of the web push key pair (Firebase console → Cloud Messaging → Web Push
 * certificates). Public by design – it lives in the code, so the CI needs no extra setting.
 */
const PUBLIC_VAPID_KEY =
  'BClZyGlqbcheGXarQVKIV651Kq-CrOmIPcNcZ6hR1eDdq1VvgevbW4YlZ7QW10SQGEa6k08X49si3yYKVAa44vA';
const VAPID =
  (import.meta.env.VITE_FIREBASE_VAPID_KEY as string | undefined) || PUBLIC_VAPID_KEY || undefined;
const ID_KEY = 'shh.push.id';

export type PushState = 'on' | 'off' | 'denied' | 'unsupported' | 'install';

/** iPhone/iPad: push only works for the app added to the home screen (iOS 16.4+). */
export const needsInstall = () =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) &&
  !(
    (navigator as Navigator & { standalone?: boolean }).standalone ||
    window.matchMedia('(display-mode: standalone)').matches
  );

const storedId = () => {
  try {
    return localStorage.getItem(ID_KEY);
  } catch {
    return null;
  }
};

export async function pushState(): Promise<PushState> {
  // the local emulators cannot deliver push – nothing to offer there
  if (useEmulators) return 'unsupported';
  if (needsInstall()) return 'install';
  if (!VAPID || !('Notification' in window) || !('serviceWorker' in navigator))
    return 'unsupported';
  if (!(await isSupported().catch(() => false))) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.permission === 'granted' && storedId() ? 'on' : 'off';
}

async function hash(s: string) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return Array.from(new Uint8Array(buf).slice(0, 16), (b) => b.toString(16).padStart(2, '0')).join(
    '',
  );
}

/** Ask, register, remember – returns the new state. */
export async function enablePush(householdId: string, memberId?: string): Promise<PushState> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return permission === 'denied' ? 'denied' : 'off';
  const { app, db, auth } = firebase();
  const registration = await navigator.serviceWorker.ready;
  const token = await getToken(getMessaging(app), {
    vapidKey: VAPID,
    serviceWorkerRegistration: registration,
  });
  const id = await hash(token);
  await setDoc(doc(db, `households/${householdId}/pushTokens/${id}`), {
    token,
    uid: auth.currentUser!.uid,
    ...(memberId ? { memberId } : {}),
    device: deviceName(),
    createdAt: Date.now(),
  });
  try {
    localStorage.setItem(ID_KEY, id);
  } catch {
    /* private mode: works until the app is closed */
  }
  return 'on';
}

export async function disablePush(householdId: string): Promise<PushState> {
  const id = storedId();
  const { app, db } = firebase();
  if (id)
    await deleteDoc(doc(db, `households/${householdId}/pushTokens/${id}`)).catch(() => undefined);
  await deleteToken(getMessaging(app)).catch(() => undefined);
  try {
    localStorage.removeItem(ID_KEY);
  } catch {
    /* ignore */
  }
  return 'off';
}

/** "iPhone", "Android", "Computer" – so parents recognise their devices. */
function deviceName() {
  const ua = navigator.userAgent;
  return /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : 'Computer';
}
